import { useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtDate, inr, STATUS_LABEL } from "@/lib/api";
import { startPayment } from "@/lib/pay";
import { printPackageDoc } from "@/lib/invoice";
import { useSettings } from "@/components/site/Footer";
import { useMember } from "@/pages/Member";
import { Card } from "@/components/member/MemberOverview";
import { PackageCard } from "@/components/mlm/PackageCard";

const ADDR = [["full_name", "Receiver name"], ["mobile", "Mobile"], ["address", "Delivery address"], ["district", "District"], ["state", "State"], ["pincode", "PIN code"]];
const STEPS = ["awaiting_payment", "paid", "processing", "packed", "shipped", "delivered"];

const usePay = (reload) => {
  const { getToken } = useAuth();
  return async (po) => {
    try {
      const res = await startPayment({ base: `/mlm/package-orders/${po.id}`, headers: { Authorization: `Bearer ${await getToken()}` }, description: po.package_name });
      if (res.status === "paid") toast.success("Payment verified – invoice generated");
      else if (res.status === "verify_error") toast.error(res.error);
    } catch (e) {
      toast.error(errMsg(e));
    }
    reload();
  };
};

const Checkout = ({ pkg, profile, call, onPlaced, onCancel }) => {
  const [qty, setQty] = useState(1);
  const [ship, setShip] = useState(() => Object.fromEntries(ADDR.map(([k]) => [k, profile[k] || ""])));
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const place = async () => {
    setBusy(true);
    try {
      onPlaced(await call("post", "/mlm/package-orders", { package_id: pkg.id, quantity: qty, ship_to: ship, accept_terms: accept }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title={`Checkout – ${pkg.name}`} testid="package-checkout">
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <span>{inr(pkg.price)} ×</span>
        <select value={qty} onChange={(e) => setQty(Number(e.target.value))} className="h-9 rounded-md border border-input bg-white px-2" data-testid="checkout-qty">{[1, 2, 3, 4, 5].map((n) => <option key={n}>{n}</option>)}</select>
        <span className="font-mono-num text-lg font-bold" data-testid="checkout-total">= {inr(pkg.price * qty)}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{ADDR.map(([k, l]) => <Input key={k} placeholder={l} value={ship[k]} onChange={(e) => setShip({ ...ship, [k]: e.target.value })} className={k === "address" ? "sm:col-span-2" : ""} data-testid={`checkout-${k}`} />)}</div>
      <label className="mt-4 flex items-start gap-2 text-sm"><Checkbox checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="checkout-accept" /><span>I accept the <a href="/policies/distributor-refund" target="_blank" rel="noreferrer" className="underline">Distributor Refund & Cancellation Policy</a>.</span></label>
      <div className="mt-4 flex gap-2">
        <Button className="rounded-full bg-amber-600 hover:bg-amber-700" disabled={!accept || busy} onClick={place} data-testid="checkout-place-order">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Place order</Button>
        <Button variant="outline" className="rounded-full" onClick={onCancel}>Back</Button>
      </div>
    </Card>
  );
};

const PayButton = ({ po, enabled, pay }) => (enabled
  ? <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700" onClick={() => pay(po)} data-testid={`pay-${po.order_number}`}>{po.payment_status === "failed" ? "Retry payment" : "Pay now"}</Button>
  : <span className="text-xs text-amber-800" data-testid={`pay-disabled-${po.order_number}`}>Online payment opens after payment-provider approval</span>);

const OrderRow = ({ po, enabled, pay, call, reload, company }) => {
  const cancel = async () => {
    if (!window.confirm("Cancel this unpaid order?")) return;
    try {
      await call("post", `/mlm/package-orders/${po.id}/cancel`);
      toast.success("Order cancelled");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const idx = STEPS.indexOf(po.status);
  const unpaid = po.payment_status !== "paid" && po.status === "awaiting_payment";
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-4 text-sm" data-testid={`package-order-${po.order_number}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <span><span className="font-semibold">{po.order_number}</span> · {po.package_name} × {po.quantity || 1} · {fmtDate(po.created_at)}</span>
        <span className="font-mono-num font-bold">{inr(po.amount)} · <span className="font-sans font-semibold">{STATUS_LABEL[po.status] || po.status}</span></span>
      </div>
      {idx >= 0 && <div className="flex flex-wrap gap-1">{STEPS.map((s, i) => <span key={s} className={`rounded-full px-2 py-0.5 text-xs ${i <= idx ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-400"}`}>{STATUS_LABEL[s] || s}</span>)}</div>}
      {po.tracking && <p>Tracking: <b>{po.tracking}</b></p>}
      <div className="flex flex-wrap items-center gap-2">
        {unpaid && <PayButton po={po} enabled={enabled} pay={pay} />}
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => printPackageDoc(po, company)} data-testid={`invoice-${po.order_number}`}><Printer className="h-4 w-4" /> {po.invoice_no ? `Invoice ${po.invoice_no}` : "Order confirmation"}</Button>
        {unpaid && <Button size="sm" variant="ghost" className="rounded-full text-red-600" onClick={cancel} data-testid={`cancel-${po.order_number}`}>Cancel</Button>}
      </div>
    </div>
  );
};

export const MemberBuy = () => {
  const [d] = useMember("/mlm/me/dashboard");
  const [o, reload, call] = useMember("/mlm/me/orders");
  const [pkgs, setPkgs] = useState([]);
  const [pick, setPick] = useState(null);
  const [placed, setPlaced] = useState(null);
  const company = useSettings();
  const pay = usePay(reload);
  useEffect(() => {
    api.get("/mlm/packages").then((r) => setPkgs(r.data));
  }, []);
  if (!d || !o) return <p className="text-slate-500">Loading…</p>;
  const enabled = d.settings.package_payments_enabled;
  return (
    <div className="space-y-6" data-testid="member-buy">
      {!enabled && <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900" data-testid="package-payments-disabled">You can place package orders now. Online payment will open after our payment partner approves it in writing — your unpaid orders stay saved and you can pay from Purchase history then.</p>}
      {placed ? (
        <Card title="Order placed" testid="order-confirmation">
          <p className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Order <b data-testid="confirmation-order-number">{placed.order_number}</b> for {placed.package_name} × {placed.quantity} ({inr(placed.amount)}) is linked to your Distributor ID {placed.distributor_id}.</p>
          <div className="mt-3 flex flex-wrap gap-2"><PayButton po={placed} enabled={enabled} pay={(po) => pay(po).then(() => setPlaced(null))} /><Button size="sm" variant="outline" className="rounded-full" onClick={() => setPlaced(null)}>Done</Button></div>
        </Card>
      ) : pick ? (
        <Checkout pkg={pick} profile={d.profile} call={call} onCancel={() => setPick(null)} onPlaced={(po) => { setPick(null); setPlaced(po); reload(); }} />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{pkgs.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} action={<Button className="w-full rounded-full bg-slate-900 hover:bg-amber-600" onClick={() => setPick(p)} data-testid={`order-package-${i}`}>Order this package</Button>} />)}</div>
      )}
      <Card title="Purchase history" testid="member-package-orders">
        <div className="space-y-3">{o.package_orders.length ? o.package_orders.map((po) => <OrderRow key={po.id} po={po} enabled={enabled} pay={pay} call={call} reload={reload} company={company} />) : <p className="text-sm text-slate-500">No package orders yet.</p>}</div>
      </Card>
    </div>
  );
};
